// @thanks https://www.youtube.com/watch?v=zLtK9lG3maw
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NgControl, ReactiveFormsModule } from '@angular/forms';

import { InputValueTypes } from '../input.component';

type Callback = (value: InputValueTypes[] | InputValueTypes) => void;
@Component({
  selector: 'evc-options-group',
  imports: [ReactiveFormsModule],
  templateUrl: './options-group.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OptionsGroupComponent implements ControlValueAccessor, OnInit {
  type = input<'checkbox' | 'radio' | 'switch'>('checkbox');
  name = input<string>('');
  label = input<string>();
  disabled = input<boolean>(false);
  internalDisabled = signal<boolean>(this.disabled());
  value = input<InputValueTypes | InputValueTypes[]>(
    this.type() === 'checkbox' || this.type() === 'switch' ? [] : true,
  );
  internalValue = signal<InputValueTypes>(this.value()! as InputValueTypes);
  internalValues = signal<InputValueTypes[]>(this.value()! as InputValueTypes[]);

  readonly #changeDetectorRef = inject(ChangeDetectorRef);
  readonly #controlDir = inject(NgControl, { optional: true, self: true });

  #onChange: Callback = () => undefined;
  #onTouch: () => void = () => undefined;

  writeValue(value: InputValueTypes | InputValueTypes[]): void {
    if (this.type() === 'checkbox' || this.type() === 'switch') {
      this.internalValues.set(value as InputValueTypes[]);
    } else {
      this.internalValue.set(value as InputValueTypes);
    }
    this.#changeDetectorRef.markForCheck();
  }
  registerOnChange(fn: Callback): void {
    this.#onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.#onTouch = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.internalDisabled.set(isDisabled);
  }

  isSelected(selectedValue: InputValueTypes): boolean {
    if (this.type() === 'checkbox' || this.type() === 'switch') {
      return this.internalValues().indexOf(selectedValue) >= 0;
    } else {
      return this.internalValue() === selectedValue;
    }
  }

  toggleValue(selectedValue: InputValueTypes): void {
    if (this.type() === 'checkbox' || this.type() === 'switch') {
      const value = this.internalValues();
      const index = value.indexOf(selectedValue);

      let updatedValues: InputValueTypes[] = [];
      this.internalValues.update((currentValues) => {
        updatedValues =
          index < 0
            ? [...currentValues, selectedValue]
            : [...currentValues.slice(0, index), ...currentValues.slice(index + 1)];

        return updatedValues;
      });

      this.#onChange(updatedValues);
    } else {
      this.internalValue.set(selectedValue);
      this.#onChange(selectedValue as InputValueTypes);
    }
    this.#onTouch();

    this.#changeDetectorRef.markForCheck();
  }

  constructor() {
    if (this.#controlDir) {
      this.#controlDir.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.#controlDir?.statusChanges?.subscribe(() => {
      this.#changeDetectorRef.markForCheck();
    });
  }
}
